import { useContext, useEffect, useMemo, useState, useRef } from 'react';
import { Button, Input, UncontrolledTooltip } from 'src/components/ui';
import getPathFromId from '../../util/getPathFromId';
import useIsDisabled from '../../util/useIsDisabled';
import ErrorBoundary from '../ErrorBoundary';
import reactTraverse from '../../util/reactTraverse';
import Jnx, { parseJnxExpr } from '../../util/jnx';
import useOptionsLookup from './hooks/useOptionsLookup';
import { useSideChannelSubscription } from '../../util/useSideChannel';
import { makeTreeNode, organizeTreeNodes, visitTrees } from '../CollapsableTreeSelect';
import Loader from '../Loader';
import classnames from 'src/util/classnames';
import { Trans } from '@lingui/react/macro';
import Icon from 'src/components/Icon';
import useInterpolatedText from './utils/useInterpolatedText';
import UWEFormContext from '../../context/UWEFormContext';

const REQUIRED_IF_JNX = jnx => {
  if (jnx) {
    jnx = parseJnxExpr(jnx);
    jnx.expr = `($isTruthy(${jnx.expr}) and $isEmpty($value)) ? true : false`;

    return new Jnx(jnx);
  }

  return false;
};

const ADDITIONAL_PROPERTY_FLAG = '__additional_property';
const REQUIRED_FIELD_SYMBOL = '*';

function SchemaFieldTemplate(props) {
  const {
    children,
    classNames,
    description,
    disabled,
    displayLabel,
    errors,
    help,
    rawErrors,
    hidden,
    id,
    label,
    onRemoveProperty,
    readonly,
    required: propRequired,
    schema,
    uiSchema,
    registry,
    fieldPathId,
  } = props;

  const formContext = registry?.formContext;

  const { hidden: schemaHidden } = schema;

  const { onLocateField } = formContext;

  const [isHovered, setIsHovered] = useState(false);

  const path = useMemo(() => getPathFromId(id), [id]);
  const rootFormData = useSideChannelSubscription(formContext?.sideChannel, 0);
  const { currentEntity } = useContext(UWEFormContext) ?? {};
  const interpolationBindings = useMemo(() => ({ currentEntity }), [currentEntity]);

  useEffect(() => {
    const visibility = formContext?.formFields?.current;

    if (visibility && path) {
      visibility[path] = true;

      return () => {
        visibility[path] = false;
      };
    }
  }, [id, formContext]);

  useIsDisabled({ id, schema, uiSchema, disabled, readonly, formContext });

  const {
    'akc:requiredIfVisible': requiredIfVisible,
    'akc:requiredIf': requiredIfExpr,
    'ui:labelSuffix': labelSuffix,
    'ui:hideLabel': hideLabel,
    'ui:tooltipText': tooltipText = '',
    'ui:hideRequiredSymbol': hideRequiredSymbol,
    'ui:field': uiField,
    'ui:staticText': _static,
    classNames: _classNames,
    ':classNames': cns,
    'ui:classNames': uiCns,
    'ui:cellClassName': uiCellCN,
  } = uiSchema;

  const tooltipRef = useRef(null);
  const required = requiredIfVisible || propRequired || REQUIRED_IF_JNX(requiredIfExpr);
  const shouldHide = uiField !== 'ComputedField';
  const evaluatedTooltipText = useInterpolatedText(tooltipText, rootFormData, path, formContext, interpolationBindings);
  const evaluatedLabel = useInterpolatedText(label, rootFormData, path, formContext, interpolationBindings);

  // Hooks stay above the early returns so a field toggling hidden keeps the same hook order.
  const [isHighlighthed, setHighlighthed] = useState(0);
  const hasAdditional = !!schema[ADDITIONAL_PROPERTY_FLAG];

  const fieldLocator = useMemo(() => {
    return onLocateField
      ? {
          onMouseEnter(e) {
            setHighlighthed(h => h + 1);
          },
          onMouseLeave(e) {
            setHighlighthed(h => h - 1);
          },
          onClick(e) {
            const topmostSftNode = reactTraverse(e.target)
              .filter(fn => fn.elementType?.name === SchemaFieldTemplate.name)
              .pop();
            onLocateField(topmostSftNode.memoizedProps.id);
          },
        }
      : {};
  }, [onLocateField, setHighlighthed]);

  useEffect(() => {
    if (!onLocateField) setHighlighthed(0);
  }, [onLocateField]);

  if (schemaHidden && shouldHide) {
    return null;
  }

  if (hidden) {
    return <div className="hidden">{children}</div>;
  }

  const field = (
    <ErrorBoundary>
      {displayLabel && !hideLabel && (
        <>
          <Label
            classNames={_static ? classnames(classNames, _classNames, cns, uiCns, uiCellCN) : ''}
            label={evaluatedLabel}
            suffix={labelSuffix}
            required={required}
            hideRequiredSymbol={hideRequiredSymbol}
            id={id}
            tooltipText={evaluatedTooltipText}
            tooltipRef={tooltipRef}
          />
          {help || null}
          {schema?.description ? description : null}
        </>
      )}
      {children}
      {rawErrors?.length ? errors : null}
    </ErrorBoundary>
  );

  // V2 grid positioning — colStart/colSpan/rowStart/rowSpan are injected into
  // the field's uiSchema by parseUISchemaFromSchema (strips leading ':').
  const { colStart, colSpan, rowStart, rowSpan } = uiSchema;
  const gridStyle =
    colStart != null || colSpan != null
      ? {
          gridColumn: colStart != null ? `${colStart} / span ${colSpan ?? 12}` : `span ${colSpan ?? 12}`,
          ...(rowStart != null || rowSpan != null
            ? {
                gridRow: rowStart != null ? `${rowStart} / span ${rowSpan ?? 1}` : `span ${rowSpan ?? 1}`,
              }
            : {}),
        }
      : undefined;

  return (
    <div
      className={classnames('schema-field', classNames, { 'is-highlighted': isHighlighthed })}
      style={gridStyle}
      {...fieldLocator}
      onMouseEnter={e => {
        setIsHovered(true);
        fieldLocator.onMouseEnter?.(e);
      }}
      onMouseLeave={e => {
        setIsHovered(false);
        fieldLocator.onMouseLeave?.(e);
      }}
    >
      {hasAdditional ? (
        <div className="row items-center" style={{ maxWidth: '25rem' }}>
          <div className="w-full flex" style={{ justifyContent: 'flex-end' }}>
            <Button
              className="text-danger schema-field-delete-btn"
              style={{ visibility: isHovered ? 'visible' : 'hidden' }}
              color="clear"
              size="sm"
              disabled={disabled || readonly}
              onClick={onRemoveProperty}
              tabIndex="-1"
            >
              <Icon icon="X" weight="regular" /> <Trans>Delete</Trans>
            </Button>
          </div>
          <div className="px-2 mr-2" style={{ flex: '1 0' }}>
            <FieldKeyLabel {...props} />
          </div>
          {field}
        </div>
      ) : (
        field
      )}
    </div>
  );
}

function useParsedOptions(options, { parentId, id, label }) {
  const tsOptions = useMemo(() => {
    if (!options) return null;
    const tsOptions = options.map(item => makeTreeNode(item, id, label, parentId));

    if (parentId) {
      const rootNodes = organizeTreeNodes(tsOptions);
      visitTrees(rootNodes);
      tsOptions.sort((a, b) => a.visitIdx - b.visitIdx);
    }

    return tsOptions;
  }, [options, parentId]);

  return tsOptions;
}

function FieldKeyLabel(props) {
  const { id, onKeyRename, onKeyRenameBlur, required, schema, registry } = props;
  const formContext = registry?.formContext;
  let { label } = props;
  let { hideRequiredSymbol } = props.uiSchema;

  const keyFromLookup = schema?.keyFromLookup?.lookup;
  const keyLabel = schema?.keyLabel ?? 'Key'; // i18n ?
  const keyId = `${id}-key`;

  const keyValue = useMemo(() => getPathFromId(id), [id]);

  if (label === ' ' && keyValue && keyFromLookup) {
    label = keyValue;
  }

  return (
    <div className="form-group w-full">
      <Label label={keyLabel} required={required} hideRequiredSymbol={hideRequiredSymbol} id={keyId} />
      {!keyFromLookup ? (
        <LabelInput label={label} required={required} hideRequiredSymbol={hideRequiredSymbol} id={keyId} onBlur={onKeyRenameBlur} />
      ) : (
        <LabelInputSelect
          formContext={formContext}
          required={required}
          hideRequiredSymbol={hideRequiredSymbol}
          lookup={keyFromLookup}
          id={id}
          label={label}
          onChange={onKeyRename}
        />
      )}
    </div>
  );
}

function LabelInputSelect({ lookup, id, label, onChange, formContext, required }) {
  const { sideChannel } = formContext;

  const noValue = '';

  const rootFormData = useSideChannelSubscription(sideChannel, 0);

  const { options: lookupOptions, loadingOptions } = useOptionsLookup({
    lookup,
    rootFormData,
    formContext,
  });

  const options = useParsedOptions(lookupOptions, lookup);

  return loadingOptions ? (
    <Loader dots />
  ) : (
    <div className="input-wrapper">
      <Input type="select" className="form-control" required={required} id={id} value={label} onChange={e => onChange(e.target.value)}>
        <option value={undefined}>{noValue}</option>
        {(options || []).map((option, idx) => (
          <option key={idx} value={option.id}>
            {option.label}
          </option>
        ))}
      </Input>
    </div>
  );
}

function Label({ classNames, label, suffix, required, hideRequiredSymbol, id, tooltipText = '', tooltipRef = null }) {
  const tooltip = () => {
    if (!tooltipText) return null;

    const lines = tooltipText.split('\n');
    return (
      <span ref={tooltipRef} className="field-label-tooltip shrink-0 cursor-help items-center">
        <Icon icon="Info" size={14} />
        <UncontrolledTooltip target={tooltipRef}>
          {lines.map((ln, ix) => (
            <div key={ln + ix} className="mb-2">
              <small>
                <em style={{ fontWeight: 500 }}>{ix + 1}.</em>
              </small>{' '}
              {ln}
            </div>
          ))}
        </UncontrolledTooltip>
      </span>
    );
  };

  return label ? (
    <label className={classnames('control-label flex flex-row items-center gap-1', classNames)} htmlFor={id}>
      {label}
      {suffix || null}
      {required && !hideRequiredSymbol && <span className="required">{REQUIRED_FIELD_SYMBOL}</span>} {tooltip()}
    </label>
  ) : null;
}

function LabelInput({ id, label, onBlur }) {
  return <input className="form-control" type="text" id={id} onBlur={onBlur} defaultValue={label} />;
}

export default SchemaFieldTemplate;
